package uk.co.nickthecoder.kyd.json

import uk.co.nickthecoder.glok.backend.Resources
import uk.co.nickthecoder.glok.scene.Color
import uk.co.nickthecoder.glok.text.BitmapFont
import uk.co.nickthecoder.glok.text.BitmapGlyphData
import uk.co.nickthecoder.glok.text.FontIdentifier
import uk.co.nickthecoder.glok.text.FontStyle
import uk.co.nickthecoder.kojak.JsonArray
import uk.co.nickthecoder.kojak.JsonObject
import uk.co.nickthecoder.kojak.JsonString
import uk.co.nickthecoder.kyd.*
import uk.co.nickthecoder.kyd.appearance.*
import uk.co.nickthecoder.kyd.shapes.*
import uk.co.nickthecoder.kyd.util.Vector2

/**
 * While Kyd is in early development, the file format is still evolving, and backwards compatibility
 * is NOT supported.
 *
 * Once the file format is stable, any breaking changes must be placed in a NEW implementation.
 */
open class GameFileVersion1 : GameFileVersion {

    override val version = "1"


    // region == Game (public) ==
    override fun gameFromJson(jGame: JsonObject, filePath: String, resources: Resources, editor: Boolean): Game {

        return Game(filePath, resources).apply {

            // Plain attributes
            title = jGame.getRequiredString(this, "title")
            initialActPath = jGame.getString("initialAct", "")
            nominalSize = jGame.getVector2("nominalSize", Vector2(1000f, 800f))
            viewPinRatio = jGame.getVector2("viewPinRatio", Vector2(0.5f, 0.5f))
            sizeConstraint = jGame.getEnum("sizeConstraint", SizeConstraint.NONE)
            managementType = jGame.getString("management", ManagementClassName)
            enableZOrder = jGame.getBoolean("enableZOrder", false)

            // jars
            for (jJar in jGame.getStrings("jars")) {
                (jJar as? JsonString)?.let { jars.add(it.string) }
            }

            // stage descriptions
            for (jStageDescription in jGame.getObjects("stageDescriptions")) {
                stageDescriptions.add(stageDescriptionFromJson(jStageDescription))
            }
            addChildrenFromJson(jGame)

            // Pick Groups
            if (editor) {
                for (jPickGroup in jGame.getObjects("pickGroups")) {
                    pickGroups.add(pickGroupFromJson(jPickGroup, game = this))
                }
            }

            // Renamed resources.
            for (jRename in jGame.getObjects("renamed")) {
                val oldPath = jRename.getString("old", "")
                val newPath = jRename.getString("new", "")
                renamedPaths[oldPath] = newPath
            }
        }

    }

    override fun Game.toJson(): JsonObject {

        val jGame = JsonObject().apply {
            // Plain attributes
            add("version", version)
            add("title", title)
            add("initialAct", initialActPath)
            add("nominalSize", nominalSize)
            addSkippingDefault("viewPinRatio", viewPinRatio, Vector2(0.5f, 0.5f))
            add("sizeConstraint", sizeConstraint)
            addSkippingDefault("management", managementType, Management::class.simpleName !!)
            addSkippingDefault("enableZOrder", enableZOrder, false)

            // jars
            if (jars.isNotEmpty()) {
                add("jars", JsonArray().apply {
                    for (jar in jars) {
                        add(jar)
                    }
                })
            }

            // stage descriptions
            val jStageDescriptions = JsonArray()
            for (stageDescription in stageDescriptions) {
                jStageDescriptions.add(stageDescription.toJson())
            }
            add("stageDescriptions", jStageDescriptions)

            addJsonChildren(this)

            // Pick Groups
            if (pickGroups.isNotEmpty()) {
                val jPickGroups = JsonArray()
                for (pickGroup in pickGroups) {
                    jPickGroups.add(pickGroup.toJson())
                }
                add("pickGroups", jPickGroups)
            }

            // Renamed resources
            if (renamedPaths.isNotEmpty()) {
                val jRenamed = JsonArray()
                for ((oldPath, newPath) in renamedPaths) {
                    jRenamed.add(JsonObject().apply {
                        add("old", oldPath)
                        add("new", newPath)
                    })
                }
                add("renamed", jRenamed)
            }
        }

        return jGame
    }
    // endregion == Game ==

    // region == StageDescription ==

    protected fun stageDescriptionFromJson(jStageDescription: JsonObject): StageDescription {
        return StageDescription().apply {
            stageName = jStageDescription.getString("stageName", "main")
            linked = jStageDescription.getBoolean("linked", true)
            stageType = jStageDescription.getString("stageType", "uk.co.nickthecoder.kyd.Stage")
        }
    }

    protected fun StageDescription.toJson(): JsonObject {
        return JsonObject().apply {
            add("stageName", stageName)
            add("linked", linked)
            add("stageType", stageType)
        }
    }

    // endregion StageDescription

    // region == Folder ==
    protected fun folderFromJson(jFolder: JsonObject, parent: ParentResource): Folder {

        return Folder(parent, jFolder.getName(parent)).apply {
            addChildrenFromJson(jFolder)
        }

    }

    protected fun Folder.toJson(): JsonObject {

        return JsonObject().apply {
            add("name", name)

            addJsonChildren(this)
        }

    }
    // endregion == Folder ==

    // region == Role ==

    protected fun roleFromJson(jRole: JsonObject, parent: ParentResource): Role {
        val name = jRole.getName(parent)
        return Role(parent, name).apply {

            defaultAppearancePath = jRole.getString("initialAppearance", "")
            defaultStageName = jRole.getString("stageName", "")
            defaultScale = jRole.getVector2("scale", Vector2.ONE)
            defaultAngleDegrees = jRole.getFloat("angle", 0f)
            defaultZOrder = jRole.getFloat("zOrder", 0f)
            behaviourType = jRole.getString("behaviour", NoBehaviourClassName)

            // Attributes
            (jRole.get("attributes") as? JsonObject)?.let { jAttributes ->
                for (attributeName in jAttributes.names()) {
                    val jAtt = jAttributes.get(attributeName) as? JsonObject ?: continue
                    val type = jAtt.getString("type", "?")
                    val att = when (type) {

                        AttributeType.STRING.name -> {
                            val meaning = jAtt.getString("meaning", AttributeMeaning.NONE.name)
                            when (meaning) {
                                AttributeMeaning.ACT.name -> AttributeValue(
                                    jAtt.getString("value", ""),
                                    AttributeMeaning.ACT
                                )

                                else -> AttributeValue(jAtt.getString("value", ""))
                            }
                        }

                        AttributeType.FLOAT.name -> AttributeValue(jAtt.getFloat("value", 0f))
                        AttributeType.INT.name -> AttributeValue(jAtt.getInt("value", 0))
                        AttributeType.BOOLEAN.name -> AttributeValue(jAtt.getBoolean("value", false))
                        AttributeType.VECTOR2.name -> AttributeValue(jAtt.getVector2("value", Vector2.ZERO))
                        else -> continue
                    }
                    attributes[attributeName] = att
                }
            }

            addChildrenFromJson(jRole)
        }
    }

    protected fun Role.toJson(): JsonObject {
        return JsonObject().apply {
            add("name", name)
            addSkippingDefault("initialAppearance", defaultAppearancePath, "")
            addSkippingDefault("stageName", defaultStageName, "")
            addSkippingDefault("scale", defaultScale, Vector2.ONE)
            addSkippingDefault("angle", defaultAngleDegrees, 0f)
            addSkippingDefault("zOrder", defaultZOrder, 0f)
            addSkippingDefault("behaviour", behaviourType, NoBehaviourClassName)

            // Attributes
            if (attributes.isNotEmpty()) {
                val jAttributes = JsonObject()
                for ((name, av) in attributes) {
                    val type = av.type
                    val v = av.value
                    val jAtt = JsonObject().apply {
                        add("type", av.type.name)
                        when (type) {
                            AttributeType.STRING -> {
                                add("value", v as String)
                                addSkippingDefault("meaning", av.meaning.name, AttributeMeaning.NONE.name)
                            }

                            AttributeType.FLOAT -> add("value", v as Float)
                            AttributeType.INT -> add("value", v as Int)
                            AttributeType.BOOLEAN -> add("value", v as Boolean)
                            AttributeType.VECTOR2 -> add("value", v as Vector2)
                        }
                    }
                    jAttributes.add(name, jAtt)
                }
                add("attributes", jAttributes)
            }

            addJsonChildren(this)
        }
    }
    // endregion Role

    // region == Graphic ==
    protected fun graphicFromJson(jGraphic: JsonObject, parent: ParentResource): Graphic {
        val name = jGraphic.getName(parent)
        val filename = jGraphic.getRequiredString(parent, "filename")
        return Graphic(parent, name, filename).apply {

            repeating = jGraphic.getBoolean("repeating", false)
            pin = jGraphic.getVector2("pin", Vector2.ZERO)

            (jGraphic.get("atlasGrid") as? JsonObject)?.let {
                atlasGrid = atlasGridFromJson(it)
            }

            addChildrenFromJson(jGraphic)
        }
    }

    protected fun Graphic.toJson(): JsonObject {
        return JsonObject().apply {
            add("name", name)
            add("filename", filename)

            addSkippingDefault("repeating", repeating, false)
            add("pin", pin)
            atlasGrid?.let { add("atlasGrid", it.toJson()) }

            addJsonChildren(this)
        }
    }
    // endregion == Graphic ==

    // region == AtlasGrid ==
    protected fun atlasGridFromJson(jAtlasGrid: JsonObject): AtlasGrid {

        return AtlasGrid().apply {
            marginX = jAtlasGrid.getInt("marginX", 0)
            marginY = jAtlasGrid.getInt("marginY", 0)
            spacing = jAtlasGrid.getInt("spacing", 0)

            poseWidth = jAtlasGrid.getInt("poseWidth", 0)
            poseHeight = jAtlasGrid.getInt("poseHeight", 0)

        }
    }

    protected fun AtlasGrid.toJson(): JsonObject {
        return JsonObject().apply {
            add("marginX", marginX)
            add("marginY", marginY)
            add("spacing", spacing)
            add("poseWidth", poseWidth)
            add("poseHeight", poseHeight)
        }
    }
    // endregion == AtlasGrid ==

    // region == Pose ==
    protected fun poseFromJson(jPose: JsonObject, parent: ParentResource): Pose {

        val name = jPose.getName(parent)
        val graphicPath = jPose.getRequiredString(parent, "graphicPath")

        return Pose(parent, name, graphicPath).apply {
            graphicTop = jPose.getInt("top", 0)
            graphicLeft = jPose.getInt("left", 0)
            graphicWidth = jPose.getInt("width", 0)
            graphicHeight = jPose.getInt("height", 0)
            pin = jPose.getVector2("pin", Vector2.ZERO)
            repeating = jPose.getBoolean("repeating", false)

            addChildrenFromJson(jPose)
        }

    }

    protected fun Pose.toJson(): JsonObject {
        return JsonObject().apply {
            add("name", name)
            add("graphicPath", graphic.path())

            add("top", graphicTop)
            add("left", graphicLeft)
            add("width", graphicWidth)
            add("height", graphicHeight)
            add("pin", pin)
            addSkippingDefault("repeating", repeating, false)

            addJsonChildren(this)
        }
    }

    // endregion == Pose ==

    // region == NinePatch ==
    protected fun ninePatchFromJson(jNinePatch: JsonObject, parent: ParentResource): NinePatch {

        val name = jNinePatch.getName(parent)
        val graphicPath = jNinePatch.getRequiredString(parent, "graphicPath")

        return NinePatch(parent, name, graphicPath).apply {
            graphicTop = jNinePatch.getInt("top", 0)
            graphicLeft = jNinePatch.getInt("left", 0)
            graphicWidth = jNinePatch.getInt("width", 0)
            graphicHeight = jNinePatch.getInt("height", 0)
            pin = jNinePatch.getVector2("pin", Vector2.ZERO)

            (jNinePatch.get("margins") as? JsonObject)?.let {
                margins = marginsFromJson(it)
            }

            addChildrenFromJson(jNinePatch)
        }

    }

    protected fun NinePatch.toJson(): JsonObject {
        return JsonObject().apply {
            add("name", name)
            add("graphicPath", graphic.path())

            add("top", graphicTop)
            add("left", graphicLeft)
            add("width", graphicWidth)
            add("height", graphicHeight)
            add("pin", pin)

            add("margins", margins.toJson())

            addJsonChildren(this)
        }
    }

    protected fun marginsFromJson(jMargins: JsonObject): Margins {
        val top = jMargins.getFloat("top", 0f)
        val right = jMargins.getFloat("right", 0f)
        val bottom = jMargins.getFloat("bottom", 0f)
        val left = jMargins.getFloat("left", 0f)
        return Margins(top, right, bottom, left)
    }

    protected fun Margins.toJson(): JsonObject {
        return JsonObject().apply {
            add("top", top)
            add("right", right)
            add("bottom", bottom)
            add("left", left)
        }
    }

    // endregion == NinePatch ==

    // region == Font ==

    protected fun fontFromJson(jFont: JsonObject, parent: ParentResource): Font {

        val name = jFont.getName(parent)

        return Font(parent, name).apply {
            family = jFont.getString("family", "")
            size = jFont.getFloat("size", 12f)
            style = FontStyle.entries.firstOrNull { it.name == jFont.getString("style", "") } ?: FontStyle.PLAIN

            spacingX = jFont.getInt("spacingX", 2)
            spacingY = jFont.getInt("spacingY", 2)

            paddingTop = jFont.getInt("paddingTop", 0)
            paddingRight = jFont.getInt("paddingRight", 0)
            paddingBottom = jFont.getInt("paddingBottom", 0)
            paddingLeft = jFont.getInt("paddingLeft", 0)

            extraAdvance = jFont.getInt("extraAdvance", 0)
            extraHeight = jFont.getInt("extraHeight", 0)

            height = jFont.getFloat("height", 1f)
            ascent = jFont.getFloat("ascent", 0f)
            descent = jFont.getFloat("descent", 0f)
            top = jFont.getFloat("top", 0f)
            bottom = jFont.getFloat("bottom", 0f)
            italicInverseSlope = jFont.getFloat("italicInverseSlope", 0f)

            val jGlyphs = jFont.getObjects("glyphs")
            for (jGlyph in jGlyphs) {
                jGlyph.getString("char", "").firstOrNull()?.let { char ->
                    val gd = BitmapGlyphData(
                        jGlyph.getFloat("x", 0f),
                        jGlyph.getFloat("y", 0f),
                        jGlyph.getFloat("top", 0f),
                        jGlyph.getFloat("right", 0f),
                        jGlyph.getFloat("bottom", 0f),
                        jGlyph.getFloat("left", 0f),
                        jGlyph.getFloat("advance", 0f)
                    )
                    //println("GD for '$char' = $gd")
                    glyphDataMap[char] = gd
                }
            }

            val texture = parent.game().resources.loadTexture("${parent.pathWithTrailingSlash()}${filename}")
            val id = FontIdentifier(family, size, style)
            bitmapFont = BitmapFont(id, texture, glyphDataMap, height, ascent, descent, top, bottom, italicInverseSlope)

            addChildrenFromJson(jFont)
        }

    }

    protected fun Font.toJson(): JsonObject {
        return JsonObject().apply {
            add("name", name)

            add("family", family)
            add("size", size)
            add("style", style.name)

            add("spacingX", spacingX)
            add("spacingY", spacingY)

            addSkippingDefault("paddingTop", paddingTop, 0)
            addSkippingDefault("paddingRight", paddingRight, 0)
            addSkippingDefault("paddingBottom", paddingBottom, 0)
            addSkippingDefault("paddingLeft", paddingLeft, 0)

            addSkippingDefault("extraAdvance", extraAdvance, 0)
            addSkippingDefault("extraHeight", extraHeight, 0)

            add("height", height)
            add("ascent", ascent)
            add("descent", descent)
            add("top", top)
            add("bottom", bottom)
            add("italicInverseSlope", italicInverseSlope)

            add("glyphs", JsonArray().apply {
                for ((char, gd) in glyphDataMap) {
                    if (gd == null) continue

                    add(JsonObject().apply {
                        add("char", char.toString())
                        add("x", gd.x)
                        add("y", gd.y)
                        add("top", gd.boundsTop)
                        add("right", gd.boundsRight)
                        add("bottom", gd.boundsBottom)
                        add("left", gd.boundsLeft)
                        add("advance", gd.advance)
                    })
                }
            })

            addJsonChildren(this)
        }
    }

    // endregion == Font ==

    // region == Link ==

    protected fun linkFromJson(jLink: JsonObject, parent: ParentResource): Link {

        val name = jLink.getName(parent)

        return Link(parent, name).apply {

            linkToPath = jLink.getString("link", "?")

            addChildrenFromJson(jLink)
        }

    }

    protected fun Link.toJson(): JsonObject {
        return JsonObject().apply {
            add("name", name)
            add("link", linkToPath)

            addJsonChildren(this)
        }
    }


    // endregion == Link ==

    // region == Shape ==

    protected fun shapeFromJson(jShape: JsonObject, parent: ParentResource): ShapeResource {

        val name = jShape.getName(parent)

        return ShapeResource(parent, name).apply {
            strokeWidth = jShape.getFloat("strokeWidth", 0f)
            strokeRatio = jShape.getFloat("strokeRatio", 0.5f)
            lineWidth = jShape.getFloat("lineWidth", 1f)
            capStyle = jShape.getEnum("capStyle", CapStyle.ROUND)
            joinStyle = jShape.getEnum("joinStyle", JoinStyle.ROUND)

            fill = (jShape.get("fill") as? JsonObject)?.let { paintFromJson(it) } ?: NoPaint
            stroke = (jShape.get("stroke") as? JsonObject)?.let { paintFromJson(it) } ?: NoPaint
        }
    }

    protected fun ShapeResource.toJson(): JsonObject {
        return JsonObject().apply {
            add("name", name)

            addSkippingDefault("strokeWidth", strokeWidth, 0f)
            addSkippingDefault("strokeRatio", strokeRatio, 0.5f)
            addSkippingDefault("lineWidth", lineWidth, 1f)
            addSkippingDefault("capStyle", capStyle.name, CapStyle.ROUND.name)
            addSkippingDefault("joinStyle", joinStyle.name, JoinStyle.ROUND.name)

            add("fill", fill.toJson())
            add("stroke", stroke.toJson())
        }
    }

    fun lineFromJson(jLine: JsonObject): Line {
        return Line(jLine.getVector2s("points"))
    }

    fun polygonFromJson(jLine: JsonObject): Polygon {
        return Polygon(jLine.getVector2s("points"))
    }

    fun Line.toJson() = JsonObject().apply {
        val jPoints = JsonArray().apply {
            for (point in points) {
                add(JsonObject().apply {
                    add("x", point.x)
                    add("y", point.y)
                })
            }
        }
        add("points", jPoints)
    }

    fun Polygon.toJson() = JsonObject().apply {
        val jPoints = JsonArray().apply {
            for (point in points) {
                add(JsonObject().apply {
                    add("x", point.x)
                    add("y", point.y)
                })
            }
        }
        add("points", jPoints)
    }

    // endregion == Shape ==

    // region == Fill and Stroke ==

    fun Paint.toJson() = JsonObject().apply {
        add("type", name)

        when (this@toJson) {
            is FlatPaint -> {
                add("color", color)
            }

            is TexturePaint -> {
                addSkippingDefault("tint", tint, Color.WHITE)
                addSkippingDefault("texture", graphicOrPosePath, "")
            }

            is ShellGradientPaint -> {
                add("color", color)
                add("toColor", toColor)
            }

            is ShellTexPaint -> {
                addSkippingDefault("texture", graphicOrPosePath, "")
            }
        }
    }


    fun paintFromJson(jFill: JsonObject): Paint {
        val type = jFill.getString("type", "None")
        return when (type) {
            FlatPaint.NAME -> {
                FlatPaint(jFill.getColor("color", Color.WHITE))
            }

            TexturePaint.NAME -> {
                TexturePaint().apply {
                    tint = jFill.getColor("tint", Color.WHITE)
                    graphicOrPosePath = jFill.getString("texture", "")
                }
            }

            ShellGradientPaint.NAME -> {
                ShellGradientPaint(
                    jFill.getColor("color", Color.WHITE),
                    jFill.getColor("toColor", Color.WHITE)
                )
            }

            ShellTexPaint.NAME -> {
                ShellTexPaint().apply {
                    graphicOrPosePath = jFill.getString("texture", "")
                }
            }

            else -> NoPaint
        }
    }

    // endregion

    // region == Act Resource ==

    protected fun actResourceFromJson(jAct: JsonObject, parent: ParentResource): ActResource {

        return ActResource(parent, jAct.getName(parent)).apply {

            (jAct.get("include") as? JsonArray)?.let {
                includeActPaths.addAll(it.values().filterIsInstance<JsonString>().map { it.string })
            }

            addChildrenFromJson(jAct)

        }

    }

    protected fun ActResource.toJson(): JsonObject {

        return JsonObject().apply {
            add("name", name)

            if (includeActPaths.isNotEmpty()) {
                JsonArray().apply {
                    for (inc in includeActPaths) {
                        add(inc)
                    }
                }.also { add("include", it) }
            }

            addJsonChildren(this)

        }

    }

    // endregion == Act Resource ==



    // region == ParentResource ==

    protected fun ParentResource.addChildrenFromJson(jParent: JsonObject) {

        val jChildren = jParent.get("children") as? JsonObject ?: return

        for (jRole in jChildren.getObjects("roles")) {
            add(roleFromJson(jRole, this))
        }

        for (jGraphic in jChildren.getObjects("graphics")) {
            add(graphicFromJson(jGraphic, this))
        }

        for (jPose in jChildren.getObjects("poses")) {
            add(poseFromJson(jPose, this))
        }

        for (jNinePatch in jChildren.getObjects("ninePatches")) {
            add(ninePatchFromJson(jNinePatch, this))
        }

        for (jFolder in jChildren.getObjects("folders")) {
            add(folderFromJson(jFolder, this))
        }

        for (jActs in jChildren.getObjects("acts")) {
            add(actResourceFromJson(jActs, this))
        }

        for (jFonts in jChildren.getObjects("fonts")) {
            add(fontFromJson(jFonts, this))
        }

        for (jLinks in jChildren.getObjects("links")) {
            add(linkFromJson(jLinks, this))
        }

        for (jShapes in jChildren.getObjects("shapes")) {
            add(shapeFromJson(jShapes, this))
        }

    }

    protected fun ParentResource.addJsonChildren(jParent: JsonObject) {

        val children = children.values
        val roles = children.filterIsInstance<Role>()
        val graphics = children.filterIsInstance<Graphic>()
        val poses = children.filterIsInstance<Pose>()
        val ninePatches = children.filterIsInstance<NinePatch>()
        val fonts = children.filterIsInstance<Font>()
        val folders = children.filterIsInstance<Folder>()
        val actResources = children.filterIsInstance<ActResource>()
        val linkResources = children.filterIsInstance<Link>()
        val shapeResources = children.filterIsInstance<ShapeResource>()

        if (children.isNotEmpty()) {
            jParent.add("children", JsonObject().also { jChildren ->

                if (roles.isNotEmpty()) {
                    JsonArray().apply {
                        for (role in roles) {
                            add(role.toJson())
                        }
                    }.also { jChildren.add("roles", it) }
                }

                if (graphics.isNotEmpty()) {
                    JsonArray().apply {
                        for (graphic in graphics) {
                            add(graphic.toJson())
                        }
                    }.also { jChildren.add("graphics", it) }
                }

                if (poses.isNotEmpty()) {
                    JsonArray().apply {
                        for (pose in poses) {
                            add(pose.toJson())
                        }
                    }.also { jChildren.add("poses", it) }
                }

                if (ninePatches.isNotEmpty()) {
                    JsonArray().apply {
                        for (ninePatch in ninePatches) {
                            add(ninePatch.toJson())
                        }
                    }.also { jChildren.add("ninePatches", it) }
                }

                if (fonts.isNotEmpty()) {
                    JsonArray().apply {
                        for (font in fonts) {
                            add(font.toJson())
                        }
                    }.also { jChildren.add("fonts", it) }
                }

                if (folders.isNotEmpty()) {
                    JsonArray().apply {
                        for (folder in folders) {
                            add(folder.toJson())
                        }
                    }.also { jChildren.add("folders", it) }
                }

                if (actResources.isNotEmpty()) {
                    JsonArray().apply {
                        for (act in actResources) {
                            add(act.toJson())
                        }
                    }.also { jChildren.add("acts", it) }
                }

                if (linkResources.isNotEmpty()) {
                    JsonArray().apply {
                        for (link in linkResources) {
                            add(link.toJson())
                        }
                    }.also { jChildren.add("links", it) }
                }

                if (shapeResources.isNotEmpty()) {
                    JsonArray().apply {
                        for (shape in shapeResources) {
                            add(shape.toJson())
                        }
                    }.also { jChildren.add("shapes", it) }
                }

            })
        }
    }

    // endregion == ParentResource ==

    // region == Pick Group & Pick ==

    protected fun pickGroupFromJson(jPickGroup: JsonObject, game: Game): PickGroup {
        return PickGroup().apply {
            name = jPickGroup.getString("name", "?")
            expanded = jPickGroup.getBoolean("expanded", false)

            for (jPick in jPickGroup.getObjects("picks")) {
                picks.add(pickFromJson(jPick, game))
            }
        }
    }

    protected fun PickGroup.toJson(): JsonObject {
        return JsonObject().apply {
            add("name", name)
            addSkippingDefault("expanded", expanded, false)

            if (picks.isNotEmpty()) {
                val jPicks = JsonArray()
                for (pick in picks) {
                    jPicks.add(pick.toJson())
                }
                add("picks", jPicks)
            }
        }
    }

    // TODO Why pass game?
    protected fun pickFromJson(jPick: JsonObject, game: Game): Pick {
        return Pick().apply {
            name = jPick.getString("name", "?")
            resourcePath = jPick.getString("resource", "")
            showLabel = jPick.getBoolean("showLabel", false)
        }
    }

    protected fun Pick.toJson(): JsonObject {
        return JsonObject().apply {

            add("name", name)
            addSkippingDefault("resource", resourcePath, "")
            addSkippingDefault("showLabel", showLabel, false)
        }
    }

    // endregion == Pick Group & Pick ==

    // region == Vector2 ==

    protected fun JsonObject.getVector2(name: String, defaultValue: Vector2): Vector2 {
        val x = getFloat("${name}X", defaultValue.x)
        val y = getFloat("${name}Y", defaultValue.y)
        return Vector2(x, y)
    }

    protected fun JsonObject.add(name: String, value: Vector2) {
        add("${name}X", value.x)
        add("${name}Y", value.y)
    }
    protected fun JsonObject.addSkippingDefault(name: String, value: Vector2, default: Vector2) {
        if (value != default) {
            add("${name}X", value.x)
            add("${name}Y", value.y)
        }
    }

    inline fun <reified T : Enum<T>> JsonObject.getEnum(name: String, defaultValue: T): T {
        return try {
            enumValueOf<T>(getString(name, defaultValue.name))
        } catch (e: Exception) {
            defaultValue
        }
    }

    protected fun JsonObject.add(name: String, value: Enum<*>) {
        add(name, value.name)
    }

    // endregion == Vector2 ==

}

